import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { HttpEventType } from '@angular/common/http';
import { saveAs } from 'file-saver';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Asset } from 'app/models/document';
import { Util } from 'app/models/util';
import { LstService } from './lst.service';
import { PermissionsService } from 'app/modules/admin/apps/file-manager/permissions/permissions.service';
import { DesktopAgentDetectDialogComponent } from '../modules/admin/apps/file-manager/desktop-agent/desktop-agent-detect-dialog.component';
import { DesktopAgentModalComponent } from '../modules/admin/apps/file-manager/desktop-agent/desktop-agent-modal.component';

export type DesktopAgentPopupMode = 'download' | 'login';
export type DesktopAppUrls = { linux: string; windows: string; mac: string };
export type InstallPhase = 'idle' | 'downloading' | 'downloaded' | 'installing' | 'signin' | 'ready';
export type DesktopOs = 'linux' | 'windows' | 'mac';
/** INSTALLING comes from the installer's temp listener. */
type AgentStatus = 'LOGIN' | 'LOGOUT' | 'INSTALLING' | 'UNREACHABLE';

@Injectable({ providedIn: 'root' })
export class DesktopAgentService {
    private static readonly AGENT_URLS = ['http://127.0.0.1:42424', 'http://localhost:42424'];
    private static readonly INSTALL_POLL_INTERVAL_MS = 2500;
    private static readonly INSTALL_HELP_AFTER_MS = 2 * 60 * 1000;

    private static readonly UNINSTALLED_PROBE_TIMEOUT_MS = 8000;
    private static readonly UNINSTALLED_PROBE_INTERVAL_MS = 1000;

    popupMode$ = new BehaviorSubject<DesktopAgentPopupMode>('download');
    deskAppUrls$ = new BehaviorSubject<DesktopAppUrls>({ linux: '', windows: '', mac: '' });
    /** File name for popover. */
    currentFileName$ = new BehaviorSubject<string>('');
    /** Tracks install progress for modal. */
    installPhase$ = new BehaviorSubject<InstallPhase>('idle');
    /** True once agent found reachable. */
    agentInstalled$ = new BehaviorSubject<boolean>(false);
    /** Shows "Don't see it?" help. */
    showInstallHelp$ = new BehaviorSubject<boolean>(false);
    /** Installer download percent; null if unknown. */
    downloadProgress$ = new BehaviorSubject<number | null>(null);
    /** File opened after sign-in. */
    fileOpened$ = new BehaviorSubject<boolean>(false);
    private downloadSub: Subscription | null = null;

    /** Asset to reopen after install. */
    private pendingAsset: Asset | null = null;
    private installPollTimer: ReturnType<typeof setInterval> | null = null;
    private installHelpTimer: ReturnType<typeof setTimeout> | null = null;
    private pollInFlight = false;
    /** Installer's listener answered INSTALLING. */
    private installerDetected = false;
    /** Pending file handed to agent. */
    private handOffState: 'none' | 'pending' | 'done' | 'failed' = 'none';
    private lastDownloadOs: DesktopOs | null = null;

    private detectDialogRef: MatDialogRef<DesktopAgentDetectDialogComponent> | null = null;
    private modalDialogRef: MatDialogRef<DesktopAgentModalComponent> | null = null;

    /** Shares one in-flight deep-link probe across concurrent banner mounts. */
    private uninstalledProbe: Promise<boolean> | null = null;

    constructor(
        private util: Util,
        private lstService: LstService,
        private permissionsService: PermissionsService,
        private dialog: MatDialog,
        private toastr: ToastrService,
    ) {
        this.deskAppUrls$.next(this.util.getDeskAppDownloadUrls());
    }

    /**
     * Opens file via drive:// protocol.
     * Deep link fires on click.
     * Avoids extra tab in Safari.
     * LOGIN just closes the popover.
     * @param asset - file to open
     */
    launch(asset: Asset): void {
        if (!this.isEnabled()) {
            return;
        }
        this.pendingAsset = asset;
        this.currentFileName$.next(asset.name);
        this.openDetectDialog();

        this.util.launchDesktopAgentDeepLink(asset.id);

        this.checkDesktopAgentLogin()
            .then(async (status) => {
                if (status === 'LOGIN') {
                    this.closeDetectDialog();
                    this.notifyOpening(asset.name);
                    this.pendingAsset = null;
                    return;
                }
                if (status === 'LOGOUT') {
                    this.closeDetectDialog();
                    this.popupMode$.next('login');
                    this.openInstallDialog();
                    this.startSignInTracking();
                    return;
                }
                if (status === 'INSTALLING') {
                    // Install already under way.
                    this.closeDetectDialog();
                    this.popupMode$.next('download');
                    this.openInstallDialog();
                    this.startInstallTracking();
                    return;
                }

                // Same check as banner.
                const alreadyInstalled = await this.checkAlreadyInstalled();
                this.closeDetectDialog();
                if (alreadyInstalled) {
                    this.pendingAsset = null;
                    return;
                }
                this.popupMode$.next('download');
                this.openInstallDialog();
            })
            .catch(() => {
                this.closeDetectDialog();
                this.popupMode$.next('download');
                this.openInstallDialog();
            });
    }

    /**
     * Checks if agent is fully uninstalled, for the banner.
     */
    async checkAlreadyInstalled(): Promise<boolean> {
        // Disabled: skip probe, hide banner.
        if (!this.isEnabled()) {
            return true;
        }
        const status = await this.checkDesktopAgentLogin();
        if (this.isAgentRunning(status)) {
            this.agentInstalled$.next(true);
            return true;
        }
        if (status === 'INSTALLING') {
            return false;
        }

        if (!this.uninstalledProbe) {
            this.uninstalledProbe = this.probeViaDeepLink().finally(() => {
                this.uninstalledProbe = null;
            });
        }
        return this.uninstalledProbe;
    }

    /**
     * Fires a bare drive:// link and polls for a response.
    */
    private async probeViaDeepLink(): Promise<boolean> {
        this.util.launchDesktopAgentDeepLink('');

        const deadline = Date.now() + DesktopAgentService.UNINSTALLED_PROBE_TIMEOUT_MS;
        while (Date.now() < deadline) {
            const status = await this.checkDesktopAgentLogin();
            if (this.isAgentRunning(status)) {
                this.agentInstalled$.next(true);
                return true;
            }
            if (status === 'INSTALLING') {
                // Not installed yet; recheck later.
                return false;
            }
            await this.delay(DesktopAgentService.UNINSTALLED_PROBE_INTERVAL_MS);
        }

        return false;
    }

    /** Agent itself answered, not installer. */
    private isAgentRunning(status: AgentStatus): boolean {
        return status === 'LOGIN' || status === 'LOGOUT';
    }

    /** Open-on-desktop feature flag. */
    private isEnabled(): boolean {
        return this.permissionsService.isOpenOnDesktopEnabled();
    }

    private delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    closeModal(): void {
        this.modalDialogRef?.close();
    }

    /** Opens host's LollyDrive sign-in page. */
    reopenSignIn(): void {
        window.open(`${window.location.origin}/drive-signin`, '_blank', 'noopener');
    }

    /** Opens install modal directly. */
    openInstallModal(): void {
        if (!this.isEnabled()) {
            return;
        }
        this.pendingAsset = null;
        this.currentFileName$.next('');
        this.popupMode$.next('download');
        this.openInstallDialog();
    }

    private openDetectDialog(): void {
        if (this.detectDialogRef) {
            return;
        }
        this.detectDialogRef = this.dialog.open(DesktopAgentDetectDialogComponent, {
            hasBackdrop: false,
            position: { bottom: '24px', right: '24px' },
            autoFocus: false,
        });
        this.detectDialogRef.afterClosed().subscribe(() => {
            this.detectDialogRef = null;
        });
    }

    private closeDetectDialog(): void {
        this.detectDialogRef?.close();
    }

    /** Toast shown once agent confirms LOGIN and the file is opening. */
    private notifyOpening(fileName: string): void {
        this.toastr.success(`Opening "${fileName}" in LollyDrive`);
    }

    private openInstallDialog(): void {
        if (this.modalDialogRef) {
            return;
        }
        // Lazy-load latest installer URLs.
        this.refreshDeskAppUrls();
        this.modalDialogRef = this.dialog.open(DesktopAgentModalComponent, {
            autoFocus: false,
            disableClose: true,
        });
        this.modalDialogRef.afterClosed().subscribe(() => {
            this.modalDialogRef = null;
            this.resetInstallTracking();
        });
    }

    /**
     * Starts tracking after download click.
     * Stays on Downloaded until installer opens.
     * Polls agent until it answers.
     */
    private startInstallTracking(): void {
        this.resetInstallTracking();
        this.installPhase$.next('downloaded');

        this.installHelpTimer = setTimeout(() => {
            // Installer heard from, no help.
            if (!this.installerDetected && this.installPhase$.value !== 'signin') {
                this.showInstallHelp$.next(true);
            }
        }, DesktopAgentService.INSTALL_HELP_AFTER_MS);

        this.installPollTimer = setInterval(
            () => this.pollForAgentReady(),
            DesktopAgentService.INSTALL_POLL_INTERVAL_MS,
        );
    }

    /**
     * Polls until signed in.
     * Deep link already delivered file.
     */
    private startSignInTracking(): void {
        this.resetInstallTracking();
        this.handOffState = 'done';
        this.installPollTimer = setInterval(
            () => this.pollForAgentReady(),
            DesktopAgentService.INSTALL_POLL_INTERVAL_MS,
        );
    }

    private async pollForAgentReady(): Promise<void> {
        if (this.pollInFlight) {
            return;
        }
        this.pollInFlight = true;
        let status: AgentStatus;
        try {
            status = await this.checkDesktopAgentLogin();
        } finally {
            this.pollInFlight = false;
        }

        // Modal closed mid-probe.
        if (!this.installPollTimer || status === 'UNREACHABLE') {
            return;
        }

        if (status === 'INSTALLING') {
            // Only signal for Installing.
            this.installerDetected = true;
            this.showInstallHelp$.next(false);
            if (this.installPhase$.value === 'downloaded') {
                this.installPhase$.next('installing');
            }
            return;
        }

        this.agentInstalled$.next(true);
        this.showInstallHelp$.next(false);

        // Agent keeps it until sign-in.
        if (this.pendingAsset && this.handOffState === 'none') {
            this.handOffState = 'pending';
            const accepted = await this.handOffToAgent(this.pendingAsset.id);
            this.handOffState = accepted ? 'done' : 'failed';
        }

        const signingIn = this.popupMode$.value === 'login' || this.installPhase$.value === 'signin';

        if (status === 'LOGOUT') {
            // Keep polling until signed in.
            if (this.popupMode$.value === 'download') {
                this.installPhase$.next('signin');
            }
            return;
        }

        if (this.pendingAsset && signingIn) {
            // Sign-in modal shows success.
            if (this.handOffState === 'failed') {
                this.util.launchDesktopAgentDeepLink(this.pendingAsset.id);
            }
            clearInterval(this.installPollTimer);
            this.installPollTimer = null;
            this.pendingAsset = null;
            this.fileOpened$.next(true);
            return;
        }

        if (!this.pendingAsset) {
            // Banner install: keep modal open.
            this.stopInstallTimers();
            this.installPhase$.next('ready');
            return;
        }

        // Old agent without /open; link may be blocked.
        if (this.handOffState === 'failed') {
            this.util.launchDesktopAgentDeepLink(this.pendingAsset.id);
        }
        this.notifyOpening(this.pendingAsset.name);
        this.pendingAsset = null;
        this.closeModal();
    }

    private stopInstallTimers(): void {
        if (this.installPollTimer) {
            clearInterval(this.installPollTimer);
            this.installPollTimer = null;
        }
        if (this.installHelpTimer) {
            clearTimeout(this.installHelpTimer);
            this.installHelpTimer = null;
        }
    }

    private resetInstallTracking(): void {
        this.downloadSub?.unsubscribe();
        this.downloadSub = null;
        this.stopInstallTimers();
        this.installerDetected = false;
        this.handOffState = 'none';
        this.fileOpened$.next(false);
        this.showInstallHelp$.next(false);
        this.installPhase$.next('idle');
    }

    /**
     * Passes the file to the agent over HTTP.
     * Unlike drive://, needs no user gesture,
     * so it works from a background tab.
     * @returns true if agent accepted it
     */
    private async handOffToAgent(assetId: string): Promise<boolean> {
        const query = `/open?fileId=${encodeURIComponent(assetId)}`;
        for (const baseUrl of DesktopAgentService.AGENT_URLS) {
            try {
                const response = await fetch(baseUrl + query, { method: 'GET', credentials: 'omit' });
                if (response.status === 200 && (await response.text()).includes('OPENING')) {
                    return true;
                }
            } catch {
                // Try next address.
            }
        }
        return false;
    }

    // Fetch latest installer download URLs
    private refreshDeskAppUrls(): Promise<void> {
        // Skip when feature disabled.
        if (!this.isEnabled()) {
            return Promise.resolve();
        }
        return this.lstService.getVersion('LL')
            .then((res: any) => {
                const data = this.lstService.getData(res);
                if (data) {
                    const current = this.deskAppUrls$.value;
                    this.deskAppUrls$.next({
                        windows: data.urlWin || current.windows,
                        mac: data.urlMac || current.mac,
                        linux: data.urlLin || current.linux,
                    });
                }
            })
            .catch(() => {
                // Keep fallback URLs on failure
            });
    }

    /**
     * Checks if desktop agent reachable.
     * @returns LOGIN, LOGOUT, INSTALLING, or UNREACHABLE
     */
    private async checkDesktopAgentLogin(): Promise<AgentStatus> {
        for (const baseUrl of DesktopAgentService.AGENT_URLS) {
            const status = await this.probeAgentEndpoint(baseUrl);
            if (status !== 'UNREACHABLE') {
                return status;
            }
        }

        return 'UNREACHABLE';
    }

    /**
     * Probes agent endpoint with timeout.
     * @param baseUrl - agent base URL
     * @returns LOGIN, LOGOUT, INSTALLING, or UNREACHABLE
     */
    private probeAgentEndpoint(
        baseUrl: string,
    ): Promise<AgentStatus> {
        return new Promise((resolve) => {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 2000);

            fetch(baseUrl, {
                method: 'GET',
                signal: controller.signal,
                credentials: 'omit',
            })
                .then(async (response) => {
                    clearTimeout(timeoutId);
                    if (response.status === 200) {
                        try {
                            const text = await response.text();
                            if (text.includes('INSTALLING')) {
                                resolve('INSTALLING');
                                return;
                            }
                            if (text.includes('LOGIN')) {
                                resolve('LOGIN');
                                return;
                            }
                            if (text.includes('LOGOUT')) {
                                resolve('LOGOUT');
                                return;
                            }
                        } catch (e) {
                            console.error('Error reading desktop agent response', e);
                        }
                    }
                    resolve('UNREACHABLE');
                })
                .catch(() => {
                    clearTimeout(timeoutId);
                    resolve('UNREACHABLE');
                });
        });
    }

    downloadDesktopAgent(downloadType: DesktopOs): void {
        const downloadUrl = this.normalizeDesktopDownloadUrl(this.deskAppUrls$.value[downloadType]);
        if (!downloadUrl) {
            return;
        }

        this.lastDownloadOs = downloadType;
        this.resetInstallTracking();
        this.downloadProgress$.next(null);
        this.installPhase$.next('downloading');

        const fallbackToNative = () => {
            window.open(downloadUrl, '_blank', 'noopener,noreferrer');
            this.startInstallTracking();
        };

        // Same-origin fetch gives real progress.
        this.downloadSub = this.lstService.downloadInstaller('LL', downloadType).subscribe({
            next: (event) => {
                if (event.type === HttpEventType.DownloadProgress) {
                    this.downloadProgress$.next(
                        event.total ? Math.round((event.loaded / event.total) * 100) : null,
                    );
                } else if (event.type === HttpEventType.Response && event.body) {
                    // API errors come back 200 with JSON.
                    if (!this.isInstallerBlob(event.body)) {
                        console.error('Installer endpoint returned an error, using direct link');
                        fallbackToNative();
                        return;
                    }
                    saveAs(event.body, this.installerFileName(downloadUrl, downloadType));
                    this.startInstallTracking();
                }
            },
            error: () => fallbackToNative(),
        });
    }

    /** Rejects JSON/HTML error bodies. */
    private isInstallerBlob(blob: Blob): boolean {
        const type = (blob.type || '').toLowerCase();
        return !type.includes('json') && !type.includes('html') && !type.startsWith('text/');
    }

    private installerFileName(url: string, os: DesktopOs): string {
        try {
            const last = new URL(url).pathname.split('/').pop();
            if (last) {
                return decodeURIComponent(last);
            }
        } catch {
            // Fall through to default.
        }
        return os === 'windows' ? 'LollyDrive-setup.exe' : `LollyDrive-setup-${os}`;
    }

    /** Re-download from the help link. */
    redownloadDesktopAgent(): void {
        this.downloadDesktopAgent(this.lastDownloadOs ?? 'windows');
    }

    private normalizeDesktopDownloadUrl(url: string): string {
        if (!url) {
            return '';
        }

        try {
            const parsedUrl = new URL(url, window.location.href);
            if (parsedUrl.protocol === 'http:' && window.location.protocol === 'https:') {
                parsedUrl.protocol = 'https:';
            }
            return parsedUrl.toString();
        } catch {
            return url;
        }
    }

    /** Returns current client OS. */
    getClientOS(): 'linux' | 'windows' | 'mac' | 'unknown' {
        const ua = navigator.userAgent.toLowerCase();
        const platform = (navigator.platform || '').toLowerCase();
        if (platform.includes('win') || ua.includes('windows')) return 'windows';
        if (platform.includes('mac') || ua.includes('mac os')) return 'mac';
        if (platform.includes('linux') || ua.includes('linux')) return 'linux';
        return 'unknown';
    }
}
